"use client";

import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import Link from "next/link";
import {ClipboardList, Github, Linkedin, Mail, Twitter, User} from "lucide-react";
import {Button} from "./ui/button";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";

const formSchema = z.object({
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    company: z.string().min(1, "Company name is required"),
    subject: z.string().min(1, "Subject is required"),
    message: z.string().min(1, "Message is required"),
    compliance_process: z.string().optional(),
    request_demo: z.enum(["yes", "no", "maybe"], {
        message: "Please select if you would like a demo",
    }),
});

export type ContactFormData = z.infer<typeof formSchema>;

export function Contact2Form() {
    const form = useForm<ContactFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            first_name: "",
            last_name: "",
            email: "",
            company: "",
            subject: "",
            message: "",
            compliance_process: "",
            request_demo: "no",
        },
    });

    async function onSubmit(values: ContactFormData) {
        try {
            console.log("submitted form", values);
        } catch (e) {
        }
    }

    const socials = [
        {
            title: "twitter",
            href: "https://twitter.com/mannupaaji",
            icon: <Twitter className="h-5 w-5 text-muted dark:text-muted-dark hover:text-black"/>,
        },
        {
            title: "github",
            href: "https://github.com/manuarora700",
            icon: <Github className="h-5 w-5 text-muted dark:text-muted-dark hover:text-black"/>,
        },
        {
            title: "linkedin",
            href: "https://linkedin.com/manuarora28",
            icon: <Linkedin className="h-5 w-5 text-muted dark:text-muted-dark hover:text-black"/>,
        },
    ];

    return (
        <Form {...form}>
            <div
                className="flex relative z-20 items-start w-full justify-center px-4 py-4 lg:py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
                <div className="mx-auto w-full max-w-md relative">
                    <div>
                        <h1 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
                            Contact Us
                        </h1>
                        <p className="mt-4 text-muted dark:text-muted-dark text-sm max-w-sm">
                            Get in touch with our team and we'll get back to you as soon as possible.
                        </p>
                    </div>

                    <div className="py-10">
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-8"
                        >
                            <div
                                className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                                    <User className="h-5 w-5 text-green-600"/>
                                    Personal Information
                                </h2>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="first_name"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel>First Name</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Your first name" {...field} />
                                                </FormControl>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="last_name"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel>Last Name</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Your last name" {...field} />
                                                </FormControl>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Email</FormLabel>
                                            <FormControl>
                                                <Input type="email" placeholder="Your email address" {...field} />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="company"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Company</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Your company name" {...field} />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div
                                className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                                    <Mail className="h-5 w-5 text-green-600"/>
                                    Message Details
                                </h2>
                                <FormField
                                    control={form.control}
                                    name="subject"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Subject</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Subject of your message" {...field} />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="message"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Message</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Write your message here"
                                                    className="min-h-[120px]"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div
                                className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                                    <ClipboardList className="h-5 w-5 text-green-600"/>
                                    Compliance Information
                                </h2>
                                <FormField
                                    control={form.control}
                                    name="compliance_process"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Compliance Process</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Please describe your current compliance process and challenges"
                                                    className="min-h-[120px]"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="request_demo"
                                    render={({field}) => (
                                        <FormItem className="space-y-3">
                                            <FormLabel>Would you like to request a demo?</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                    className="flex flex-col space-y-1"
                                                >
                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                        <FormControl>
                                                            <RadioGroupItem value="yes" id="demo-yes"/>
                                                        </FormControl>
                                                        <FormLabel htmlFor="demo-yes">Yes, I would like a
                                                            demo</FormLabel>
                                                    </FormItem>
                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                        <FormControl>
                                                            <RadioGroupItem value="no" id="demo-no"/>
                                                        </FormControl>
                                                        <FormLabel htmlFor="demo-no">No, just information
                                                            please</FormLabel>
                                                    </FormItem>
                                                    <FormItem className="flex items-center space-x-3 space-y-0">
                                                        <FormControl>
                                                            <RadioGroupItem value="maybe" id="demo-maybe"/>
                                                        </FormControl>
                                                        <FormLabel htmlFor="demo-maybe">Maybe later</FormLabel>
                                                    </FormItem>
                                                </RadioGroup>
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="flex flex-col items-center space-y-4">
                                <Button
                                    type="submit"
                                    className="w-full bg-green-600 hover:bg-green-700 text-white px-8"
                                    size="lg"
                                >
                                    Send Message
                                </Button>
                                <p className="text-sm text-slate-500 max-w-md text-center">
                                    By submitting this form, you agree to our <Link href="/terms"
                                                                                    className="text-green-600 hover:underline">Terms
                                    of Service</Link> and <Link href="/privacy"
                                                                className="text-green-600 hover:underline">Privacy
                                    Policy</Link>.
                                </p>
                            </div>
                        </form>
                    </div>
                    <div className="flex items-center justify-center space-x-4 py-4">
                        {socials.map((social) => (
                            <Link href={social.href} key={social.title}>
                                {social.icon}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </Form>
    );
}